/**
 * LA TARJETA «estructura_cambio» DEL ASISTENTE (23-10-2026, tanda 3b de «Campos
 * y numeración»): leerla desconfiando y guardarla con la sesión del admin.
 *
 * La edge (`tools/estructura.ts`) ya resolvió nombres y validó valores, pero la
 * tarjeta viaja por el navegador y vuelve del historial: aquí se comprueba OTRA
 * VEZ su forma (lo que no se entiende se descarta) y se escribe en este orden:
 *   1. los campos nuevos (si ya existe uno con esa etiqueta, se usa ese);
 *   2. los valores, «a mano» (`manual`: numerar no los pisa);
 *   3. las numeraciones, calculadas AHORA con la estructura y los valores de la
 *      base de datos (`planNumeracion`), como «Numerar ahora» de Ajustes.
 * La RLS exige admin en cada escritura.
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import {
  leerNumeracion,
  NIVELES,
  normalizarValor,
  TIPOS,
  VIGENCIAS,
  type CampoEstructura,
  type NivelCampo,
  type Numeracion,
  type TipoCampo,
  type VigenciaCampo,
} from "./camposEstructura";
import { cargarCampanas, cargarCampos, cargarDatosEstructura, cargarValores } from "./cargar";
import { planNumeracion } from "./planNumeracion";

export type OpTarjeta =
  | { op: "crear_campo"; etiqueta: string; nivel: NivelCampo; tipo: TipoCampo; vigencia: VigenciaCampo; en_informes: boolean; junto_al_nombre: boolean; numeracion: Numeracion | null }
  | { op: "poner_valor"; campo: string; entidad_id: string; periodo: string; valor: string; sitio: string; periodo_label: string; aproximado: boolean }
  | { op: "numerar"; campo: string; periodo: string; periodo_label: string };

export interface TarjetaEstructura {
  ops: OpTarjeta[];
  noCasan: { dicho: string; motivo: string }[];
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PERIODO = /^(|campana:[0-9a-f-]{36}|anio:\d{4})$/i;
const txt = (v: unknown, max: number) => (typeof v === "string" ? v.trim().slice(0, max) : "");
const clave = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

/** La tarjeta tal como llegó, o null si no es una tarjeta de estructura que se entienda. */
export function tarjetaEstructura(item: unknown): TarjetaEstructura | null {
  const o = item && typeof item === "object" ? (item as Record<string, unknown>) : null;
  if (!o || o.v !== 1 || !Array.isArray(o.ops)) return null;
  const ops: OpTarjeta[] = [];
  for (const raw of o.ops.slice(0, 500)) {
    const r = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
    if (r.op === "crear_campo") {
      const etiqueta = txt(r.etiqueta, 60);
      const nivel = r.nivel as NivelCampo;
      const tipo = r.tipo as TipoCampo;
      if (!etiqueta || !NIVELES.includes(nivel) || !TIPOS.includes(tipo)) continue;
      const vigencia = nivel === "cultivo" ? "fija" : VIGENCIAS.includes(r.vigencia as VigenciaCampo) ? (r.vigencia as VigenciaCampo) : "fija";
      ops.push({
        op: "crear_campo",
        etiqueta,
        nivel,
        tipo,
        vigencia,
        en_informes: r.en_informes !== false,
        junto_al_nombre: r.junto_al_nombre === true,
        numeracion: tipo === "numeracion" ? leerNumeracion(r.numeracion) : null,
      });
    } else if (r.op === "poner_valor") {
      const campo = txt(r.campo, 60);
      const valor = txt(r.valor, 200);
      const entidad = txt(r.entidad_id, 40);
      const periodo = typeof r.periodo === "string" ? r.periodo : "";
      if (!campo || !valor || !UUID.test(entidad) || !PERIODO.test(periodo)) continue;
      ops.push({ op: "poner_valor", campo, entidad_id: entidad, periodo, valor, sitio: txt(r.sitio, 160), periodo_label: txt(r.periodo_label, 60), aproximado: r.aproximado === true });
    } else if (r.op === "numerar") {
      const campo = txt(r.campo, 60);
      const periodo = typeof r.periodo === "string" ? r.periodo : "";
      if (!campo || !PERIODO.test(periodo)) continue;
      ops.push({ op: "numerar", campo, periodo, periodo_label: txt(r.periodo_label, 60) });
    }
  }
  if (ops.length === 0) return null;
  const noCasan = (Array.isArray(o.no_casan) ? o.no_casan : [])
    .slice(0, 100)
    .map((x) => (x && typeof x === "object" ? (x as Record<string, unknown>) : {}))
    .map((x) => ({ dicho: txt(x.dicho, 200), motivo: txt(x.motivo, 200) }))
    .filter((x) => x.dicho);
  return { ops, noCasan };
}

export type ResultadoAplicar =
  | { ok: true; campos: number; valores: number; numerados: number; descartados: number }
  | { ok: false; motivo: "permiso" | "invalida" | "error"; detalle?: string };

type Sb = Pick<SupabaseClient, "from">;

const esPermiso = (e: { code?: string; message?: string } | null) =>
  !!e && (e.code === "42501" || /row-level security|permission/i.test(e.message ?? ""));

export async function aplicarCambiosEstructura(sb: Sb, companyId: string, userId: string | null, item: unknown): Promise<ResultadoAplicar> {
  const t = tarjetaEstructura(item);
  if (!t) return { ok: false, motivo: "invalida" };
  try {
    // 1. Campos nuevos (el que ya existe con esa etiqueta se reutiliza).
    let campos = await cargarCampos(sb, companyId);
    const vivo = (etiqueta: string) => campos.find((c) => !c.archivado_en && clave(c.etiqueta) === clave(etiqueta));
    let creados = 0;
    let orden = campos.reduce((m, c) => Math.max(m, c.orden), -1) + 1;
    for (const o of t.ops) {
      if (o.op !== "crear_campo" || vivo(o.etiqueta)) continue;
      const { error } = await sb.from("estructura_campos").insert({
        company_id: companyId,
        etiqueta: o.etiqueta,
        nivel: o.nivel,
        tipo: o.tipo,
        vigencia: o.vigencia,
        en_informes: o.en_informes,
        junto_al_nombre: o.junto_al_nombre,
        numeracion: o.numeracion,
        orden: orden++,
      });
      if (error && error.code !== "23505") return esPermiso(error) ? { ok: false, motivo: "permiso" } : { ok: false, motivo: "error", detalle: error.message };
      if (!error) creados++;
    }
    if (creados) campos = await cargarCampos(sb, companyId);

    // 2. Valores escritos (dictados o del Excel): «a mano».
    let descartados = 0;
    const filas: Record<string, unknown>[] = [];
    for (const o of t.ops) {
      if (o.op !== "poner_valor") continue;
      const c: CampoEstructura | undefined = vivo(o.campo);
      let valor: string | null = null;
      try {
        valor = c ? normalizarValor(c.tipo, o.valor) : null;
      } catch {
        valor = null;
      }
      if (!c || !valor) {
        descartados++;
        continue;
      }
      filas.push({ company_id: companyId, campo_id: c.id, entidad_id: o.entidad_id, periodo: c.nivel === "cultivo" || c.vigencia === "fija" ? "" : o.periodo, valor, manual: true, actualizado_por: userId });
    }
    for (let i = 0; i < filas.length; i += 200) {
      const { error } = await sb.from("estructura_valores").upsert(filas.slice(i, i + 200), { onConflict: "campo_id,entidad_id,periodo" });
      if (error) return esPermiso(error) ? { ok: false, motivo: "permiso" } : { ok: false, motivo: "error", detalle: error.message };
    }

    // 3. Numeraciones, con lo que hay AHORA en la base de datos.
    let numerados = 0;
    const aNumerar = t.ops.filter((o): o is Extract<OpTarjeta, { op: "numerar" }> => o.op === "numerar");
    if (aNumerar.length) {
      const [datos, campanas] = await Promise.all([cargarDatosEstructura(sb, companyId), cargarCampanas(sb, companyId)]);
      for (const o of aNumerar) {
        const c = vivo(o.campo);
        if (!c || c.tipo !== "numeracion") {
          descartados++;
          continue;
        }
        const valores = await cargarValores(sb, companyId);
        const plan = planNumeracion(c, c.nivel !== "cultivo" && c.vigencia === "fija" ? "" : o.periodo, datos, valores, campanas);
        const escribir = plan.escribir.map((e) => ({ company_id: companyId, campo_id: c.id, entidad_id: e.entidad_id, periodo: e.periodo, valor: e.valor, manual: false, actualizado_por: userId }));
        for (let i = 0; i < escribir.length; i += 200) {
          const { error } = await sb.from("estructura_valores").upsert(escribir.slice(i, i + 200), { onConflict: "campo_id,entidad_id,periodo" });
          if (error) return esPermiso(error) ? { ok: false, motivo: "permiso" } : { ok: false, motivo: "error", detalle: error.message };
        }
        numerados += escribir.length;
      }
    }
    return { ok: true, campos: creados, valores: filas.length, numerados, descartados };
  } catch (e) {
    const err = e as { code?: string; message?: string };
    return esPermiso(err) ? { ok: false, motivo: "permiso" } : { ok: false, motivo: "error", detalle: err?.message };
  }
}
